import React, { useState, useEffect } from 'react';
import {
    Close as CloseIcon,
    Add as AddIcon,
    DeleteOutline as DeleteOutlineIcon,
    OpenInNew as OpenInNewIcon,
    Save as SaveIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import { Drawer, useMediaQuery, Chip, Tooltip, CircularProgress } from '@mui/material';
import {
    Box,
    Button,
    IconButton,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Paper,
    Alert,
    Snackbar
} from '@mui/material';
import ApiService from '../../_services/ApiService';
import useSnackDispatcher from '../../_helpers/reusablefunctions/snackBarComponent';

const mockPlatforms = [
  {
    id: 'spotify',
    name: 'Spotify',
    urlPatterns: [
      'open.spotify.com/track',
      'open.spotify.com/album',
      'open.spotify.com/playlist',
      'spotify.link'
    ],
    example: 'https://open.spotify.com/track/7ouMYWpwJ422jRcDASZB7P'
  },
  {
    id: 'applemusic',
    name: 'Apple Music',
    urlPatterns: [
      'music.apple.com',
      'itunes.apple.com'
    ],
    example: 'https://music.apple.com/us/album/shivers/1580676948?i=1580676951'
  },
  {
    id: 'youtube',
    name: 'YouTube',
    urlPatterns: [
      'youtube.com/watch',
      'm.youtube.com/watch',
      'youtu.be/'
    ],
    example: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'
  },
  {
    id: 'YouTube Music',
    name: 'YouTube Music',
    urlPatterns: [
      'music.youtube.com/watch'
    ],
    example: 'https://music.youtube.com/watch?v=dQw4w9WgXcQ'
  },
  {
    id: 'JIO SAAVN',
    name: 'JIO SAAVN',
    urlPatterns: [
      'jiosaavn.com/song',
      'jiosaavn.com/album',
      'm.jiosaavn.com/song',
      'm.jiosaavn.com/album'
    ],
    example: 'https://www.jiosaavn.com/song/shayad/ISFfUzZAWVU'
  },
  {
    id: 'Amazon Music',
    name: 'Amazon Music',
    urlPatterns: [
      'music.amazon.com/albums',
      'music.amazon.com/tracks',
      'music.amazon.in/albums',
      'music.amazon.in/tracks',
      'amazon.com/music/player',
      'music.amazon.co.uk',
      'music.amazon.co.jp'
    ],
    example: 'https://music.amazon.in/tracks/B0FJS4Q2RL'
  },
  {
    id: 'Gaana',
    name: 'Gaana',
    urlPatterns: [
      'gaana.com/song',
      'gaana.com/album',
      'gaana.com/playlist'
    ],
    example: 'https://gaana.com/album/shershaah'
  },
  {
    id: 'instagram-Make Reels',
    name: 'Instagram (Make Reel)',
    urlPatterns: [
      'instagram.com/reel',
      'www.instagram.com/reel'
    ],
    example: 'https://www.instagram.com/reel/CxZfBR0JGz3/'
  },
  {
    id: 'deezer',
    name: 'Deezer',
    urlPatterns: [
      'deezer.com/track',
      'www.deezer.com/track',
      'deezer.page.link' // Deezer short links
    ],
    example: 'https://www.deezer.com/track/3135556'
  }
];


const AddPlatformDrawer = ({ setIsPlatformOpen, isPlatformOpen, songData }) => {
    const smallViewport = useMediaQuery('(max-width:768px)');
    const [platforms, setPlatforms] = useState([]);
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState({});
    const [originalValues, setOriginalValues] = useState({});
    const { successSnack, errorSnack } = useSnackDispatcher();

    useEffect(() => {
        setTimeout(() => setPlatforms(mockPlatforms), 500);
    }, []);

    // ------------ get api -----
    const fetchLinks = async () => {
        if (!songData?.song_id) return;

        try {
            setLoading(prev => ({ ...prev, initial: true }));
            const response = await ApiService.get('getLink', { search: songData.song_id });
            if (response?.status === 200 || response?.status === 201) {
                const fetchedLinks = response.data.data.map(link => ({
                    id: Date.now() + Math.random(),
                    platformId: link.streaming_platform,
                    link: link.streaming_song_link,
                    apiId: link.id,
                    enabled: true,
                    errors: {},
                    isNew: false
                }));
                setCards(fetchedLinks);
                setOriginalValues(fetchedLinks.reduce((acc, card) => {
                    acc[card.id] = { platformId: card.platformId, link: card.link };
                    return acc;
                }, {}));
            }
        } catch (error) {
            errorSnack('Failed to fetch platform links', 'error');
        } finally {
            setLoading(prev => ({ ...prev, initial: false }));
        }
    }

    useEffect(() => {
        fetchLinks();
    }, [songData]);


    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch {
            return false;
        }
    };

    const validatePlatformUrl = (url, platformId) => {
        if (!url || !platformId) return { isValid: true, error: null };

        if (!isValidUrl(url)) {
            return { isValid: false, error: 'Please enter a valid URL' };
        }

        const platform = platforms.find(p => p.id === platformId);
        if (!platform) return { isValid: true, error: null };

        const urlLower = url.toLowerCase();
        const isValidPlatform = platform.urlPatterns.some(pattern =>
            urlLower.includes(pattern.toLowerCase())
        );

        if (!isValidPlatform) {
            return {
                isValid: false,
                error: `Please enter a valid ${platform.name} URL (e.g., ${platform.example})`
            };
        }

        return { isValid: true, error: null };
    };

    const addNewCard = () => {
        const newCard = {
            id: Date.now(),
            platformId: '',
            link: '',
            enabled: true,
            errors: {},
            isNew: true
        };
        setCards(prev => [...prev, newCard]);
        setOriginalValues(prev => ({ ...prev, [newCard.id]: { platformId: '', link: '' } }));
    };

    const updateCard = (cardId, field, value) => {
        setCards(prev => prev.map(card => {
            if (card.id !== cardId) return card;

            const updatedCard = { ...card, [field]: value };

            if (field === 'link') {
                const validation = validatePlatformUrl(value, updatedCard.platformId);
                const errors = { ...updatedCard.errors };

                if (!validation.isValid) {
                    errors.link = validation.error;
                } else {
                    delete errors.link;
                }
                updatedCard.errors = errors;
            }

            // Re-validate URL when platform changes
            if (field === 'platformId' && updatedCard.link) {
                const validation = validatePlatformUrl(updatedCard.link, value);
                const errors = { ...updatedCard.errors };

                if (!validation.isValid) {
                    errors.link = validation.error;
                } else {
                    delete errors.link;
                }
                updatedCard.errors = errors;
            }

            return updatedCard;
        }));
    };

    const getAvailablePlatforms = (currentCardId) => {
        const selectedIds = cards
            .filter(card => card.id !== currentCardId && card.platformId)
            .map(card => card.platformId);
        return platforms.filter(platform => !selectedIds.includes(platform.id));
    };

    const deleteCard = async (cardId) => {
        const card = cards.find(c => c.id === cardId);

        if (card.apiId) {
            try {
                setLoading(prev => ({ ...prev, [cardId]: true }));
                await ApiService.delete('link_creation', { id: card.apiId });
                successSnack('Platform link deleted successfully');
            } catch (error) {
                errorSnack('Failed to delete platform link', 'error');
                return;
            } finally {
                setLoading(prev => ({ ...prev, [cardId]: false }));
            }
        }

        setCards(prev => prev.filter(card => card.id !== cardId));
        setOriginalValues(prev => {
            const newValues = { ...prev };
            delete newValues[cardId];
            return newValues;
        });
    };

    const hasChanges = (card) => {
        const original = originalValues[card.id];
        if (!original) return true; // New card
        return original.platformId !== card.platformId || original.link !== card.link;
    };

    const handleSaveOrUpdate = async (card) => {
        if (!card.platformId || !card.link || card.errors.link || !hasChanges(card)) {
            return;
        }

        const cardId = card.id;
        setLoading(prev => ({ ...prev, [cardId]: true }));

        const payload = {
            song: songData?.song_id,
            streaming_platform: card.platformId,
            streaming_song_link: card.link,
            is_admin_created: 'True',
        };

        try {
            if (card.apiId) {
                await ApiService.put('link_creation', {
                    streaming_platform: card.platformId,
                    streaming_song_link: card.link
                }, { id: card.apiId });
                successSnack('Platform link updated successfully');
            } else {
                const response = await ApiService.post("link_creation", payload);
                if (response?.status === 201 || response?.status === 200) {
                    setCards(prev => prev.map(c =>
                        c.id === card.id ? { ...c, apiId: response.data.id, isNew: false } : c
                    ));
                    successSnack('Platform link created successfully');
                    // Call get API after create
                    fetchLinks();
                }
            }

            // Update original values after successful save
            setOriginalValues(prev => ({
                ...prev,
                [cardId]: { platformId: card.platformId, link: card.link }
            }));

        } catch (error) {
            errorSnack('Failed to save platform link', 'error');
        } finally {
            setLoading(prev => ({ ...prev, [cardId]: false }));
        }
    };

    const handleFieldBlur = (card, field) => {
        if (field === 'platformId' || field === 'link') {
            handleSaveOrUpdate(card);
        }
    };


    const getPlatformPlaceholder = (platformId) => {
        const platform = platforms.find(p => p.id === platformId);
        return platform ? platform.example : 'Enter platform URL';
    };

    if (!isPlatformOpen) return null;

    return (
        <>
            <Drawer
                sx={{
                    width: '500px',
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: smallViewport ? '100%' : '500px',
                        boxSizing: 'border-box',
                    },
                }}
                anchor="right"
                open={isPlatformOpen}
            >
                <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper'
                    }}>
                        <Box>
                            <Typography variant="h6" fontWeight={600}>Add Platform</Typography>
                        </Box>
                        <IconButton onClick={() => setIsPlatformOpen(false)}>
                            <CloseIcon fontSize="small" sx={{ color: 'grey.500' }} />
                        </IconButton>
                    </Box>

                    {/* Add New Button */}
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'right' }}>
                        <Button
                            onClick={addNewCard}
                            variant="contained"
                            className='gn-actionbtn'
                            startIcon={<AddIcon fontSize="small" />}
                            disabled={platforms.length === 0}
                        >
                            ADD NEW
                        </Button>
                    </Box>

                    {/* Cards List */}
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                        {cards.map((card) => {
                            const availablePlatforms = getAvailablePlatforms(card.id);
                            const isLoading = loading[card.id];
                            const hasUnsavedChanges = hasChanges(card);

                            return (
                                <Paper
                                    key={card.id}
                                    sx={{
                                        padding: 2,
                                        mb: 2,
                                        position: 'relative',
                                        border: '1px solid #e0e0e0',
                                        '&:hover': { boxShadow: 2 }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>

                                        {/* Card Content */}
                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {/* Platform Select */}
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Select Platform</InputLabel>
                                                <Select
                                                    value={card.platformId}
                                                    label="Select Platform"
                                                    onChange={(e) => updateCard(card.id, 'platformId', e.target.value)}
                                                    onBlur={() => handleFieldBlur(card, 'platformId')}
                                                    disabled={isLoading}
                                                >
                                                    <MenuItem value="">Select Platform</MenuItem>
                                                    {availablePlatforms.map((platform) => (
                                                        <MenuItem key={platform.id} value={platform.id}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {platform.name}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            {/* Link Input */}
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label="Add link"
                                                placeholder={getPlatformPlaceholder(card.platformId)}
                                                value={card.link}
                                                onChange={(e) => updateCard(card.id, 'link', e.target.value)}
                                                onBlur={() => handleFieldBlur(card, 'link')}
                                                error={!!card.errors.link}
                                                helperText={card.errors.link || (card.platformId ? `Enter a valid ${platforms.find(p => p.id === card.platformId)?.name} URL` : '')}
                                                disabled={isLoading || !card.platformId}
                                                InputProps={{
                                                    endAdornment: card.link && !card.errors.link ? (
                                                        <Tooltip title="Open link">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => window.open(card.link, '_blank')}
                                                            >
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null
                                                }}
                                            />
                                        </Box>

                                        {/* Controls */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
                                            <Tooltip title="Delete platform">
                                                <IconButton
                                                    onClick={() => deleteCard(card.id)}
                                                    size="small"
                                                    disabled={isLoading}
                                                    sx={{
                                                        color: 'error.main',
                                                        '&:hover': { bgcolor: 'error.lighter' }
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Paper>
                            );
                        })}

                        {/* Empty State */}
                        {cards.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Typography variant="h3" color="grey.400" mb={1}>🎵</Typography>
                                <Typography color="text.secondary" variant="body2" mb={1}>
                                    No platforms added yet
                                </Typography>
                                <Typography color="grey.400" variant="caption">
                                    Click "ADD NEW" to get started
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default AddPlatformDrawer;